import React from "react";
import { Box, Chip, Typography } from "@mui/material";
import InkyMascot from "../../components/brand/InkyMascot";

/**
 * Empty-thread greeting with context-aware suggestion chips. Chips seed the composer; they
 * never send on their own.
 * @param {object} props - Props.
 * @param {string} [props.greetingName] - User's first name.
 * @param {string|null} [props.contextLine] - What Inky can see (e.g. "Looking at Kickoff notes").
 * @param {Array<{label: string, prompt: string}>} props.suggestions - Suggestions.
 * @param {(prompt: string) => void} props.onPick - Seed the composer.
 * @returns {JSX.Element} Empty state.
 */
export default function InkyEmptyState({ greetingName, contextLine, suggestions, onPick }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", px: 2, pt: 4, pb: 2, gap: 1 }}>
      <Box
        sx={{
          width: 72,
          height: 72,
          borderRadius: "50%",
          bgcolor: "var(--q-accent-soft, #ECE8FC)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 0.5,
        }}
      >
        <InkyMascot size={52} mood="happy" animated decorative />
      </Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "var(--q-text, inherit)" }}>
        Hi{greetingName ? ` ${greetingName}` : ""}, I'm Inky.
      </Typography>
      <Typography variant="body2" sx={{ color: "var(--q-muted, inherit)", maxWidth: 300 }}>
        {contextLine || "I can draft and edit documents from your requirements, answer questions about your projects and meetings, and get you around Quill."}
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 0.75, mt: 1.5 }}>
        {suggestions.map((suggestion) => (
          <Chip
            key={suggestion.label}
            label={suggestion.label}
            onClick={() => onPick(suggestion.prompt)}
            variant="outlined"
            sx={{
              borderColor: "var(--q-border, #E3E0DA)",
              bgcolor: "var(--q-surface, #FFFFFF)",
              color: "var(--q-text-2, inherit)",
              fontSize: "0.8rem",
              "&:hover": { bgcolor: "var(--q-accent-tint, #F7F5FE)", borderColor: "var(--q-accent-border, #DDD7F7)" },
            }}
          />
        ))}
      </Box>
    </Box>
  );
}
